import { DEFAULT_CONVERSATION_ID } from "@repo/contract/iam";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { openConversationSocket } from "./conversation-socket";
import { fetchChatHistory } from "./history";
import { forgetLastAnswer, lastCompleteAnswer, rememberLastAnswer, } from "./last-answer";
import { buyWsTicket } from "./ws-ticket";
const EMPTY_SNAPSHOT = {
    activity: null,
    errorKey: null,
    fromCache: false,
    hydrated: false,
    messages: [],
    status: "idle",
};
/**
 * The first frame for a conversation: its device copy, unconfirmed, or the
 * empty snapshot. The socket seeds itself from the same copy, but it emits
 * nothing until its history read settles or a question is asked — this is
 * what the screen draws until then.
 */
function seededSnapshot(cache, conversationId) {
    const cached = cache?.read(conversationId) ?? null;
    if (cached === null || cached.length === 0) {
        return EMPTY_SNAPSHOT;
    }
    return {
        ...EMPTY_SNAPSHOT,
        fromCache: true,
        messages: cached.map((message) => ({ ...message, cached: true })),
    };
}
export function useTrainerChat(input) {
    const { accountId, aiBaseUrl, appActive, client, lastAnswerStore, locale, online, pendingTurnStore, transcriptCache, } = input;
    // Not a destructuring default: React Compiler 1.0 skips a whole component
    // or hook that has one (reference/repo-rules.md, found by T49/T61).
    const conversationId = input.conversationId ?? DEFAULT_CONVERSATION_ID;
    const { onAnswerComplete, subjectAccountId } = input;
    // ── THE SNAPSHOT CARRIES THE CONVERSATION IT BELONGS TO (T44) ─────────────
    // Changing `conversationId` rebuilds the socket, but the state set by the
    // OLD one survives the render in which the id changed. Read bare, the screen
    // would draw A's transcript under B for a frame, and the strip effect below
    // would write A's last answer under B's key. So the state is tagged, and a
    // snapshot for another conversation reads as the empty one until the new
    // socket emits its own.
    //
    // The fallback is the conversation's device copy rather than the empty
    // snapshot, read once per conversation: it is what makes the trainer open
    // on the transcript instead of on nothing while the history read runs.
    const seed = useMemo(() => seededSnapshot(transcriptCache, conversationId), [conversationId, transcriptCache]);
    const [owned, setOwned] = useState(() => ({ conversationId, snapshot: seed }));
    const snapshot = owned.conversationId === conversationId ? owned.snapshot : seed;
    const socketRef = useRef(null);
    /** The message whose first sentence home already has; nothing is rewritten. */
    const cachedAnswerId = useRef(null);
    // Offline boot keeps the person signed in with no account (the app's auth
    // provider); no account id, no key to write under, no write.
    // ── HOME'S STRIP IS WRITTEN FROM HERE, AS EACH ANSWER COMPLETES (`D156`) ──
    // The one rule — only a `complete` answer, never a truncated one — is in
    // `lastCompleteAnswer`, where a Node test can reach it; this effect is the
    // subscription. It runs on every snapshot, and writes once per answer: the
    // id is remembered so a re-render, a streamed delta on the NEXT turn or a
    // hydration that restores the same answer costs no storage write. The same
    // once-per-answer moment is when the screen re-marks the conversation seen.
    useEffect(() => {
        if (owned.conversationId !== conversationId) {
            return;
        }
        const answer = lastCompleteAnswer(owned.snapshot.messages);
        if (answer === null || answer.id === cachedAnswerId.current) {
            return;
        }
        cachedAnswerId.current = answer.id;
        onAnswerComplete?.(owned.conversationId);
        if (accountId === null) {
            return;
        }
        // Never throws — the store swallows a refusal — but a promise left
        // floating in an effect is a lint the next reader has to explain.
        rememberLastAnswer(accountId, owned.conversationId, answer.text, Date.now(), lastAnswerStore).catch(() => undefined);
    }, [accountId, conversationId, lastAnswerStore, onAnswerComplete, owned]);
    useEffect(() => {
        const conversation = openConversationSocket({
            aiBaseUrl,
            ...(appActive === undefined ? {} : { appActive }),
            // `D43`: the trainer answers in the user's language, and the language
            // is the APP's stored setting rather than the device's (`D42`). Passed
            // into the ticket purchase, because a WebSocket has one handshake and no
            // per-turn slot to carry it in.
            buyTicket: () => buyWsTicket(client.authorizedFetch, locale),
            conversationId,
            ...(subjectAccountId === undefined ? {} : { subjectAccountId }),
            fetchHistory: (url) => fetchChatHistory(url),
            now: Date.now,
            onChange: (id, next) => setOwned({ conversationId: id, snapshot: next }),
            ...(online === undefined ? {} : { online }),
            // The platform's `WebSocket` is a global with the handler-property shape
            // `ChatSocket` declares. The cast is the boundary between a structural
            // interface written for testability and the platform's own class.
            openSocket: (url) => new WebSocket(url),
            pendingTurnStore,
            ...(transcriptCache === undefined ? {} : { transcriptCache }),
        });
        const { trainer } = conversation;
        socketRef.current = trainer;
        // The screen rebuilds itself from the server, and reclaims an answer that
        // finished while it was gone. Not awaited: the effect may not return a
        // promise, and every failure inside is already a no-op.
        trainer.hydrate().catch(() => undefined);
        return () => {
            // The Durable Object keeps generating with nobody attached (`D11`), so
            // leaving the screen does not cancel an answer — it stops watching one.
            // Coming back opens a new socket and resumes it. `close` also silences
            // whatever of this conversation is still in flight (a history read, a
            // ticket), so a switch to another conversation hears nothing from it.
            conversation.close();
            if (socketRef.current === trainer) {
                socketRef.current = null;
            }
        };
        // `locale` is in the deps on purpose: changing the language on the settings
        // screen rebuilds the socket, so the NEXT answer is in the new language. The
        // conversation survives — it lives in the Durable Object (`D11`), not here.
        // `conversationId` is in them for the same reason: a new id is a new
        // Durable Object, so a new socket and a new history read (T44).
    }, [
        aiBaseUrl,
        appActive,
        client,
        conversationId,
        locale,
        online,
        pendingTurnStore,
        subjectAccountId,
        transcriptCache,
    ]);
    const ask = useCallback((question, options) => {
        socketRef.current?.ask(question, options ?? {});
    }, []);
    const answerAskWidget = useCallback((toolCallId, output) => {
        socketRef.current?.answerAskWidget(toolCallId, output);
    }, []);
    // `?? false`: no socket is the same answer as a refusal — nothing was told
    // to anybody, so nothing may be opened.
    const acceptScreenOffer = useCallback((toolCallId) => socketRef.current?.acceptScreenOffer(toolCallId) ?? false, []);
    const abandon = useCallback(() => {
        socketRef.current?.abandon();
    }, []);
    const resend = useCallback(() => {
        socketRef.current?.resend();
    }, []);
    const clear = useCallback(() => {
        socketRef.current?.clear();
        // The transcript is gone (`D124`); a line quoted from it on home would
        // be quoting a conversation that no longer exists.
        if (accountId !== null) {
            cachedAnswerId.current = null;
            forgetLastAnswer(accountId, conversationId, lastAnswerStore).catch(() => undefined);
        }
    }, [accountId, conversationId, lastAnswerStore]);
    // ── THE RETURNED OBJECT IS THE SCREEN'S MOST LOAD-BEARING IDENTITY ────────
    // A fresh object literal here is a new `chat` on EVERY render of
    // `ChatScreen`, and the screen's callbacks depend on it: `submitAsk`,
    // `runWidgetWrites`, `drainEchoQueue`, then `widgetPanel`, then `renderItem`.
    // React Native's `CellRenderer` is a `PureComponent` shallow-comparing `item`
    // and `renderItem`, so a churning `renderItem` re-renders EVERY visible row
    // for content that did not change — once per streamed delta, and once per
    // keystroke in the composer, since typing re-renders this screen too.
    //
    // Five of the six callbacks above are `useCallback([])` and never change,
    // and `clear` moves only with the account id, the conversation and the
    // injected store, which is once per sign-in or per switch; so the snapshot
    // is the only thing that can move in practice: this memo makes `chat`
    // change exactly when the conversation does, and not when the composer does.
    return useMemo(() => ({
        ...snapshot,
        abandon,
        acceptScreenOffer,
        answerAskWidget,
        ask,
        clear,
        resend,
    }), [abandon, acceptScreenOffer, answerAskWidget, ask, clear, resend, snapshot]);
}
//# sourceMappingURL=use-trainer-chat.js.map